import { c as _c } from "react/compiler-runtime";
import { createContext, useContext, useState } from 'react';
import useSWR, { KeyedMutator, useSWRConfig } from 'swr';
import { APIList } from '../../../../../../../typings/API.interface';
import { APIDestination } from '../../../../../../../typings/Destination.interface';
import { APISource } from '../../../../../../../typings/Source.interface';
import { APIWebhook, TransformReference } from '../../../../../../../typings/Webhook.interface';
import APIMethodKeys from '../../../../client/APIMethodKeys';
import { GlobalContext } from '../../../contexts/GlobalContext';
import useLocalStorage from '../../../hooks/useLocalStorage';
import { DashboardContext } from '../DashboardContext';
export type ResourceKey = 'sources' | 'destinations' | 'webhooks';
type DeletableResource = APIWebhook | APIDestination | APISource;
type Resource = DeletableResource;
type MutationMethods = {
  property: string;
  data: APIList<any> | undefined;
  mutate: KeyedMutator<APIList<any>>;
};
export type IResourcesContext = {
  mutateResourceType?: (resource_key: ResourceKey, resource: Resource, deleted?: boolean) => void;
  resource_mutation_methods?: {
    webhooks: MutationMethods;
    sources: MutationMethods;
    destinations: MutationMethods;
  };
  webhooks?: APIList<APIWebhook>;
  destinations?: APIList<APIDestination>;
  sources?: APIList<APISource>;
  setSearchTerm?: (term: string) => void;
  search_term?: string;
  order_by?: keyof typeof order_by_options;
  setOrderBy?: (by: keyof typeof order_by_options) => void;
  group_by?: keyof typeof group_by_options;
  setGroupBy?: (by: keyof typeof group_by_options) => void;
};
export const ResourcesContext = createContext<IResourcesContext>({});
type ResourcesFilters = {
  status?: 'disabled' | 'paused' | 'active';
  source_id?: string;
  destination_id?: string;
  limit?: number;
  next?: string;
  prev?: string;
};
const filterToAPIQuery = (filters: ResourcesFilters) => {
  if (!filters) {
    return {};
  }
  const api_filters: any = {
    ...filters
  };
  if (api_filters.status) {
    if (api_filters.status === 'paused') {
      api_filters.paused_at = {
        any: true
      };
    } else if (filters.status === 'disabled') {
      api_filters.disabled_at = {
        any: true
      };
    } else if (filters.status === 'active') {
      api_filters.disabled_at = null;
      api_filters.paused_at = null;
    } else {
      api_filters.disabled = true;
    }
    delete api_filters.status;
  } else {
    api_filters.disabled = true;
  }
  return api_filters;
};
export const group_by_options = {
  source: {
    label: 'Source',
    icon: 'source'
  },
  destination: {
    label: 'Destination',
    icon: 'destination'
  }
} as const;
export const order_by_options = {
  full_name_asc: {
    label: 'A -> Z',
    order_by: {
      source: 'full_name',
      destination: 'full_name',
      none: 'full_name'
    },
    dir: 'asc'
  },
  full_name_desc: {
    label: 'Z -> A',
    order_by: {
      source: 'full_name',
      destination: 'full_name',
      none: 'full_name'
    },
    dir: 'desc'
  },
  last_updated: {
    label: 'Last updated',
    order_by: {
      source: ['sources.updated_at', 'updated_at'],
      destination: ['destinations.updated_at', 'updated_at'],
      none: 'updated_at'
    },
    dir: 'desc'
  },
  newest: {
    label: 'Newest -> Oldest',
    order_by: {
      source: ['sources.created_at', 'created_at'],
      destination: ['destinations.created_at', 'created_at'],
      none: 'created_at'
    },
    dir: 'desc'
  },
  oldest: {
    label: 'Oldest -> Newest',
    order_by: {
      source: ['sources.created_at', 'created_at'],
      destination: ['destinations.created_at', 'created_at'],
      none: 'created_at'
    },
    dir: 'asc'
  }
} as const;
export const ResourcesContextProvider: React.FC<{
  filters: ResourcesFilters;
  children: React.ReactNode | ((context: IResourcesContext) => React.ReactNode);
  view: 'structured' | 'table';
}> = t0 => {
  const $ = _c(31);
  const {
    children,
    filters,
    view
  } = t0;
  const api_filters = filterToAPIQuery(filters);
  const {
    HookdeckAPI
  } = useContext(GlobalContext);
  const {
    team
  } = useContext(DashboardContext);
  const [search_term, setSearchTerm] = useState("");
  const {
    mutate: globalMutate
  } = useSWRConfig();
  const [order_by, setOrderBy] = useLocalStorage("pref:sort_connections", "newest");
  const [group_by, setGroupBy] = useLocalStorage("pref:group_by_connections", "source");
  const getSearchPropertyFromTerm = _temp;
  const order_by_key = view === "structured" ? group_by : "none";
  const formatted_filters = {
    limit: 200,
    order_by: order_by_options[order_by].order_by[order_by_key],
    dir: order_by_options[order_by].dir,
    ...api_filters
  };
  if (search_term) {
    const search_property = getSearchPropertyFromTerm(search_term);
    formatted_filters[search_property] = search_term;
  }
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      keepPreviousData: true,
      dedupingInterval: 150
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    data: webhooks,
    mutate: mutateWebhooks
  } = useSWR(APIMethodKeys.webhooks.list(formatted_filters), () => HookdeckAPI.webhooks.list(formatted_filters), t1);
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = APIMethodKeys.transformations.list();
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== HookdeckAPI.transformations) {
    t3 = () => HookdeckAPI.transformations.list();
    $[2] = HookdeckAPI.transformations;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      keepPreviousData: true,
      dedupingInterval: 150
    };
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const {
    data: transformations,
    mutate: mutateTransformations
  } = useSWR(t2, t3, t4);
  let t5;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = APIMethodKeys.destinations.list();
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] !== HookdeckAPI.destinations) {
    t6 = () => HookdeckAPI.destinations.list();
    $[6] = HookdeckAPI.destinations;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  let t7;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = {
      keepPreviousData: true,
      dedupingInterval: 150
    };
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  const {
    data: destinations,
    mutate: mutateDestinations
  } = useSWR(t5, t6, t7);
  let t8;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = APIMethodKeys.sources.list();
    $[9] = t8;
  } else {
    t8 = $[9];
  }
  let t9;
  if ($[10] !== HookdeckAPI.sources) {
    t9 = () => HookdeckAPI.sources.list();
    $[10] = HookdeckAPI.sources;
    $[11] = t9;
  } else {
    t9 = $[11];
  }
  let t10;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = {
      keepPreviousData: true,
      dedupingInterval: 150
    };
    $[12] = t10;
  } else {
    t10 = $[12];
  }
  const {
    data: sources,
    mutate: mutateSources
  } = useSWR(t8, t9, t10);
  let t11;
  if ($[13] !== children || $[14] !== destinations || $[15] !== globalMutate || $[16] !== group_by || $[17] !== mutateDestinations || $[18] !== mutateSources || $[19] !== mutateTransformations || $[20] !== mutateWebhooks || $[21] !== order_by || $[22] !== search_term || $[23] !== setGroupBy || $[24] !== setOrderBy || $[25] !== setSearchTerm || $[26] !== sources || $[27] !== team || $[28] !== transformations?.models || $[29] !== webhooks) {
    const resource_mutation_methods = {
      webhooks: {
        property: "webhook",
        data: webhooks,
        mutate: mutateWebhooks
      },
      sources: {
        property: "source",
        data: sources,
        mutate: mutateSources
      },
      destinations: {
        property: "destination",
        data: destinations,
        mutate: mutateDestinations
      }
    };
    const mutateWebhooksResource = (resource_key, resource) => {
      const property = resource_mutation_methods[resource_key].property;
      const models = webhooks.models.map(webhook => webhook[property].id === resource.id ? {
        ...webhook,
        [property]: resource
      } : webhook);
      return mutateWebhooks({
        ...webhooks,
        models
      }, true);
    };
    const mutateResourceType = async (resource_key_0, resource_0, t12, t13) => {
      const deleted = t12 === undefined ? false : t12;
      const include_auth = t13 === undefined ? true : t13;
      const {
        mutate
      } = resource_mutation_methods[resource_key_0];
      if (!mutate || !resource_0) {
        return;
      }
      if (resource_key_0 === "sources" || resource_key_0 === "destinations") {
        const key = include_auth ? APIMethodKeys[resource_key_0].get(resource_0.id, {
          include: "config.auth"
        }) : APIMethodKeys[resource_key_0].get(resource_0.id);
        await globalMutate([key, team.id], resource_0);
      }
      if (resource_key_0 === "webhooks") {
        if ("source" in resource_0) {
          mutateResourceType("sources", resource_0.source, false, false);
        }
        if ("destination" in resource_0) {
          mutateResourceType("destinations", resource_0.destination, false, false);
        }
        if ("rules" in resource_0) {
          const transformation_rule = resource_0.rules.find(_temp2) as TransformReference | undefined;
          if (transformation_rule && !transformations?.models?.some(transformation => transformation_rule.transformation_id === transformation.id)) {
            mutateTransformations();
          }
        }
      } else {
        mutateWebhooksResource(resource_key_0, resource_0 as APIDestination | APISource);
      }
      return mutate(data => {
        let models_0 = data.models;
        if (deleted) {
          models_0 = data.models.filter(model => model.id !== resource_0.id);
        } else {
          const index = data.models.findIndex(model_0 => model_0.id === resource_0.id);
          if (index >= 0) {
            models_0[index] = resource_0;
          } else {
            models_0 = [resource_0, ...data.models];
          }
        }
        return {
          count: models_0.length,
          pagination: {
            ...data.pagination,
            prev: models_0[0]?.id,
            next: models_0[models_0.length - 1]?.id
          },
          models: models_0
        };
      }, true);
    };
    const context = {
      mutateResourceType,
      resource_mutation_methods,
      webhooks,
      sources,
      destinations,
      setSearchTerm,
      search_term,
      setOrderBy,
      order_by,
      setGroupBy,
      group_by
    };
    t11 = <ResourcesContext.Provider value={context}>{typeof children === "function" ? children(context) : children}</ResourcesContext.Provider>;
    $[13] = children;
    $[14] = destinations;
    $[15] = globalMutate;
    $[16] = group_by;
    $[17] = mutateDestinations;
    $[18] = mutateSources;
    $[19] = mutateTransformations;
    $[20] = mutateWebhooks;
    $[21] = order_by;
    $[22] = search_term;
    $[23] = setGroupBy;
    $[24] = setOrderBy;
    $[25] = setSearchTerm;
    $[26] = sources;
    $[27] = team;
    $[28] = transformations?.models;
    $[29] = webhooks;
    $[30] = t11;
  } else {
    t11 = $[30];
  }
  return t11;
};
function _temp(term) {
  if (term.startsWith("web_")) {
    return "id";
  }
  if (term.startsWith("src_")) {
    return "source_id";
  }
  if (term.startsWith("des_")) {
    return "destination_id";
  }
  return "full_name";
}
function _temp2(rule) {
  return rule.type === "transform";
}